import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import StageSelector from "@/components/StageSelector";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Loader2 } from "lucide-react";

export default function RequestJobDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [ms, setMs] = useState("");
  const [target, setTarget] = useState("");
  const [selected, setSelected] = useState(STAGES);
  const [custom, setCustom] = useState([]);

  const mutation = useMutation({
    mutationFn: async (payload) => (await api.post("/jobs/request", payload)).data,
    onSuccess: () => {
      toast.success("Job request submitted");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setOpen(false);
      setMs(""); setTarget(""); setSelected(STAGES); setCustom([]);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ms_description: ms,
      selected_stages: selected,
      custom_stages: custom,
      target_completion_date: target || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="request-job-button"><Plus className="h-4 w-4 mr-2" /> Request a Job</Button>
      </DialogTrigger>
      <DialogContent data-testid="request-job-dialog" className="max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-heading">Request a new job</DialogTitle>
          <DialogDescription>Describe your job, pick the works needed, and propose a completion date. Our team will review and activate it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Job / Method Statement description</Label>
            <Textarea value={ms} onChange={(e) => setMs(e.target.value)} required rows={3} className="mt-1.5"
              placeholder="Describe the job you need…" data-testid="request-ms-input" />
          </div>
          <StageSelector selected={selected} setSelected={setSelected} custom={custom} setCustom={setCustom} />
          <div>
            <Label>Proposed completion date</Label>
            <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5" data-testid="request-target-date" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="submit-request-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit Request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
